import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PositionTypeEnum } from 'service/trading-demo';

export interface OrderSuccessPopupType {
  type: PositionTypeEnum;
  symbol: string;
  qty: number;
  price: string;
}

const OrderSuccessPopup = ({
  data,
  onClose,
}: {
  data: OrderSuccessPopupType;
  onClose: () => void;
}) => {
  const { type, symbol, qty, price } = data;
  const [showCheck, setShowCheck] = useState(false);

  useEffect(() => {
    // Show check animation after a short delay
    const checkTimer = setTimeout(() => {
      setShowCheck(true);
    }, 200);

    // Auto close after 2 seconds
    const closeTimer = setTimeout(() => {
      onClose();
    }, 1500);

    return () => {
      clearTimeout(checkTimer);
      clearTimeout(closeTimer);
    };
  }, [onClose]);

  if (!data) return null;
  const actionText = type === PositionTypeEnum.Long ? '買い' : '売り';
  const actionColor = type === PositionTypeEnum.Long ? '#00C853' : '#EF4444';

  return (
    <div className="all-font-sans pointer-events-none fixed inset-0 z-[9999] flex items-center justify-center">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 10 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
            duration: 0.4,
          }}
          className="pointer-events-auto"
        >
          <div className="min-w-[280px] max-w-[320px] rounded-2xl bg-white p-6 text-center shadow-2xl">
            {/* Icon with animation */}
            <div
              className="relative mx-auto mb-4 flex size-12 items-center justify-center rounded-full"
              style={{ background: actionColor }}
            >
              <motion.svg
                viewBox="0 0 24 24"
                className="size-6 text-white"
                initial={{ scale: 0, opacity: 0 }}
                animate={
                  showCheck
                    ? { scale: 1, opacity: 1 }
                    : { scale: 0, opacity: 0 }
                }
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 15,
                  delay: 0.2,
                }}
              >
                <motion.polyline
                  points="20 6 9 17 4 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={showCheck ? { pathLength: 1 } : { pathLength: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.3,
                    ease: 'easeInOut',
                  }}
                />
              </motion.svg>

              {/* Ripple effect */}
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-white"
                initial={{ scale: 1, opacity: 1 }}
                animate={
                  showCheck
                    ? { scale: 2, opacity: 0 }
                    : { scale: 1, opacity: 1 }
                }
                transition={{
                  duration: 0.6,
                  delay: 0.2,
                  ease: 'easeOut',
                }}
              />
            </div>

            {/* Title */}
            <motion.div
              className="mb-2 text-lg font-bold text-gray-800"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.3 }}
            >
              {actionText}注文約定
            </motion.div>

            {/* Details */}
            <motion.div
              className="mb-1 text-base font-semibold text-gray-700"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.3 }}
            >
              {symbol} - 数量：{qty}
            </motion.div>

            {/* Price */}
            <motion.div
              className="text-sm font-medium text-gray-500"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.3 }}
            >
              {price}
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default OrderSuccessPopup;
